/**
 * NIL Go submission router (US-033).
 *
 * Three handlers in v1:
 *   getReadiness(dealId)    — composite checklist consumed by the deal
 *                             detail page's readiness panel.
 *   previewSubmission(dealId) — returns the exact payload that will be
 *                             sent, for the confirm-before-submit modal.
 *   submit(dealId)          — validates, calls the configured provider,
 *                             creates a NilGoSubmission row, returns the
 *                             provider's `exportBundle` (when present) so
 *                             the UI can trigger a download.
 *
 * The router uses `context.db` for all reads — pristine separation from
 * `@app/db`'s module-load side effects (see the audit.ts docstring for
 * the node:* leak this avoids).
 *
 * @package @app/api
 */

import { getNilGoProvider } from '@app/nil-go';
import { type NilGoSubmissionPayload, nilGoSubmissionPayloadSchema } from '@app/validators';
import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { writeAuditLog } from '../lib/audit';
import { protectedProcedure } from '../middleware/auth';
import {
  computeNilGoReadiness,
  type ReadinessInput,
  type ReadinessResult,
} from '../services/nil-go-readiness';
import {
  computePayorAggregateExposure,
  computeUnsubmittedReportableDealsForPayor,
} from '../services/payor-aggregation';

// biome-ignore lint/suspicious/noExplicitAny: Prisma client + nested includes
type Db = any;

/**
 * Pull every field the readiness service + payload preview need. The same
 * snapshot powers both endpoints so the preview can't disagree with the
 * checklist.
 */
async function loadDealSnapshot(db: Db, dealId: string, athleteId: string) {
  const deal = await db.deal.findFirst({
    where: { id: dealId, athleteId },
    include: {
      payor: true,
      compensationItems: { orderBy: { orderIndex: 'asc' } },
      deliverables: {
        orderBy: { dueDate: 'asc' },
        include: { proofs: { orderBy: { capturedAt: 'desc' } } },
      },
    },
  });
  if (!deal) throw new ORPCError('NOT_FOUND', { message: 'Deal not found' });

  const athlete = await db.user.findUnique({
    where: { id: athleteId },
    select: {
      id: true,
      name: true,
      email: true,
      ncaaId: true,
      classYear: true,
      sport: true,
      school: true,
      eligibilityVerifiedAt: true,
      eligibilityVerificationSource: true,
    },
  });
  if (!athlete) throw new ORPCError('NOT_FOUND', { message: 'Athlete not found' });

  return { deal, athlete };
}

function readinessInputFromSnapshot(
  deal: any,
  athlete: any,
  payorAggregateExposure: number | null,
  unsubmittedReportableWithPayor: ReadinessInput['unsubmittedReportableWithPayor'] = [],
): ReadinessInput {
  return {
    deal: {
      id: deal.id,
      contractUrl: deal.contractUrl,
      signedDate: deal.signedDate?.toISOString() ?? null,
    },
    athlete: {
      ncaaId: athlete.ncaaId,
      eligibilityVerifiedAt: athlete.eligibilityVerifiedAt?.toISOString() ?? null,
    },
    payor: deal.payor
      ? {
          id: deal.payor.id,
          legalName: deal.payor.legalName,
          affiliation: deal.payor.affiliation,
          validationStatus: deal.payor.validationStatus,
        }
      : null,
    compensationItems: deal.compensationItems.map((it: any) => ({
      type: it.type,
      amount: Number(it.amount),
    })),
    deliverables: deal.deliverables.map((d: any) => ({
      activityCategory: d.activityCategory,
      metadata: d.metadata,
    })),
    // US-033.3: aggregate exposure across all this athlete's deals with
    // this payor in the current tax year. `null` when no payor is set,
    // since aggregation is per-payor by definition.
    payorAggregateExposure,
    unsubmittedReportableWithPayor,
  };
}

/** US-033.3 helper — fetch the aggregate exposure for a deal's payor if
 * one is set. Always computes for the CURRENT calendar year so the "YTD
 * with this payor" line in the panel description means YTD (year to
 * date) the way an athlete reads it: this year, not the year of the
 * deal. Moving a deal's signedDate to a prior year correctly drops it
 * out of this number → it goes to $0 when the athlete has no other
 * current-year deals with the payor. The cross-year unsubmitted-
 * reportable banner (computed separately via
 * `computeUnsubmittedReportableDealsForPayor`) handles the "you still
 * owe a 2025 report" obligation regardless of calendar year. */
async function fetchAggregateExposureForDeal(
  db: Db,
  deal: any,
  athleteId: string,
): Promise<number | null> {
  if (!deal.payor?.id) return null;
  const exposure = await computePayorAggregateExposure({
    db,
    athleteId,
    payorId: deal.payor.id,
  });
  return exposure.total;
}

function buildPayload(deal: any, athlete: any): NilGoSubmissionPayload {
  const submittedAt = new Date().toISOString();
  const totalCashEquivalent = deal.compensationItems.reduce(
    (s: number, it: any) => s + Number(it.amount || 0),
    0,
  );

  if (!deal.payor) {
    // The submission flow gates on readiness before we reach buildPayload,
    // so a null payor here is a contract violation — surface explicitly.
    throw new ORPCError('BAD_REQUEST', {
      message: 'Deal has no payor; complete the readiness checklist before previewing.',
    });
  }
  if (!athlete.ncaaId) {
    throw new ORPCError('BAD_REQUEST', {
      message: 'Athlete has no NCAA ID; add it on /profile before previewing.',
    });
  }

  const payload = {
    schemaVersion: 1 as const,
    submittedAt,
    deal: {
      id: deal.id,
      title: deal.title,
      description: deal.description ?? null,
      brandName: deal.brandName,
      currency: deal.currency,
      totalCashEquivalent,
      startDate: deal.startDate?.toISOString() ?? null,
      endDate: deal.endDate?.toISOString() ?? null,
      signedDate: deal.signedDate?.toISOString() ?? null,
      isExclusive: deal.isExclusive,
      exclusivityTerms: deal.exclusivityTerms ?? null,
      notes: deal.notes ?? null,
      contractUrl: deal.contractUrl ?? null,
    },
    athlete: {
      name: athlete.name,
      email: athlete.email,
      ncaaId: athlete.ncaaId,
      classYear: athlete.classYear ?? null,
      sport: athlete.sport ?? null,
      school: athlete.school ?? null,
      eligibilityVerifiedAt: athlete.eligibilityVerifiedAt?.toISOString() ?? null,
      eligibilityVerificationSource: athlete.eligibilityVerificationSource ?? null,
    },
    payor: {
      legalName: deal.payor.legalName,
      displayName: deal.payor.displayName ?? null,
      ein: deal.payor.ein ?? null,
      affiliation: deal.payor.affiliation as 'unknown' | 'associated' | 'non_associated',
      address: {
        line1: deal.payor.addressLine1 ?? null,
        line2: deal.payor.addressLine2 ?? null,
        city: deal.payor.city ?? null,
        state: deal.payor.state ?? null,
        postalCode: deal.payor.postalCode ?? null,
        country: deal.payor.country ?? 'US',
      },
      industry: deal.payor.industry ?? null,
      contactName: deal.payor.contactName ?? null,
      contactEmail: deal.payor.contactEmail ?? null,
      website: deal.payor.website ?? null,
      validationStatus: deal.payor.validationStatus,
      validationProvider: deal.payor.validationProvider ?? null,
    },
    activities: deal.deliverables.map((d: any) => ({
      activityCategory: d.activityCategory ?? 'ENDORSEMENT',
      title: d.title,
      description: d.description ?? null,
      dueDate: d.dueDate?.toISOString() ?? null,
      status: d.status,
      metadata: typeof d.metadata === 'string' ? safeJsonParse(d.metadata) : (d.metadata ?? null),
      proofs: (d.proofs ?? []).map((p: any) => ({
        type: p.type,
        url: p.url,
        capturedAt: p.capturedAt.toISOString(),
      })),
    })),
    compensation: deal.compensationItems.map((it: any) => ({
      type: it.type,
      amount: Number(it.amount),
      currency: it.currency,
      description: it.description,
      royaltyPercent: it.royaltyPercent !== null ? Number(it.royaltyPercent) : null,
      expectedVolumeLow: it.expectedVolumeLow !== null ? Number(it.expectedVolumeLow) : null,
      expectedVolumeHigh: it.expectedVolumeHigh !== null ? Number(it.expectedVolumeHigh) : null,
      unitLabel: it.unitLabel ?? null,
      dueDate: it.dueDate?.toISOString() ?? null,
    })),
    // FMV is wired in by US-033.1's panel via the existing ai.estimateDealValue
    // endpoint; the snapshot allows it to be missing on first ship.
    fmv: null,
  };

  // Re-validate at the boundary so the shape can't drift.
  return nilGoSubmissionPayloadSchema.parse(payload);
}

function safeJsonParse(s: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(s);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export const nilGoRouter = {
  getReadiness: protectedProcedure
    .input(z.object({ dealId: z.string() }))
    .handler(async ({ context, input }): Promise<ReadinessResult> => {
      const { db, session } = context;
      const { deal, athlete } = await loadDealSnapshot(db, input.dealId, session.user.id);
      const [aggregate, unsubmittedReportable] = await Promise.all([
        fetchAggregateExposureForDeal(db, deal, session.user.id),
        deal.payor?.id
          ? computeUnsubmittedReportableDealsForPayor({
              db,
              athleteId: session.user.id,
              payorId: deal.payor.id,
            })
          : Promise.resolve([]),
      ]);
      return computeNilGoReadiness(
        readinessInputFromSnapshot(deal, athlete, aggregate, unsubmittedReportable),
      );
    }),

  previewSubmission: protectedProcedure
    .input(z.object({ dealId: z.string() }))
    .handler(async ({ context, input }): Promise<NilGoSubmissionPayload> => {
      const { db, session } = context;
      const { deal, athlete } = await loadDealSnapshot(db, input.dealId, session.user.id);
      return buildPayload(deal, athlete);
    }),

  submit: protectedProcedure
    .input(
      z.object({
        dealId: z.string(),
        /** Athletes can submit a not_reportable deal explicitly via this
         * flag — same shape as the "Submit anyway" UI link in US-033.1. */
        force: z.boolean().optional().default(false),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const { deal, athlete } = await loadDealSnapshot(db, input.dealId, session.user.id);
      const aggregate = await fetchAggregateExposureForDeal(db, deal, session.user.id);
      const readiness = computeNilGoReadiness(readinessInputFromSnapshot(deal, athlete, aggregate));

      if (readiness.status === 'needs_attention') {
        throw new ORPCError('BAD_REQUEST', {
          message: 'Deal is not Ready. Complete the readiness checklist before submitting.',
        });
      }
      if (readiness.status === 'not_reportable' && !input.force) {
        throw new ORPCError('BAD_REQUEST', {
          message:
            'Deal is under the $600 NIL Go threshold. Pass force=true to opt in to reporting.',
        });
      }

      const payload = buildPayload(deal, athlete);
      const provider = getNilGoProvider();

      const { createId } = await import('@app/db');
      let providerResult;
      try {
        providerResult = await provider.submit(payload);
      } catch (e) {
        throw new ORPCError('INTERNAL_SERVER_ERROR', {
          message: `Provider ${provider.id} failed to submit: ${e instanceof Error ? e.message : String(e)}`,
        });
      }

      // Determine revision number — increments per attempt for the deal.
      const lastRevision = await db.nilGoSubmission.findFirst({
        where: { dealId: deal.id },
        orderBy: { revisionNumber: 'desc' },
        select: { revisionNumber: true },
      });
      const revisionNumber = (lastRevision?.revisionNumber ?? 0) + 1;

      const row = await db.nilGoSubmission.create({
        data: {
          id: createId(),
          dealId: deal.id,
          athleteId: athlete.id,
          status: providerResult.nextStatus,
          revisionNumber,
          submittedAt: new Date(),
          externalSubmissionId: providerResult.externalSubmissionId,
          payloadSnapshot: JSON.stringify(payload),
          provider: provider.id,
          auditLog: providerResult.auditNote ?? null,
        },
      });

      await writeAuditLog({
        action: 'nil_go.submitted',
        category: 'compliance',
        actor: {
          id: session.user.id,
          name: session.user.name ?? null,
          email: session.user.email ?? null,
          role: session.user.role ?? null,
        },
        target: { type: 'nilGoSubmission', id: row.id, name: deal.title },
        context: {
          dealId: deal.id,
          revisionNumber,
          provider: provider.id,
          status: providerResult.nextStatus,
        },
      });

      return {
        submissionId: row.id,
        status: providerResult.nextStatus,
        revisionNumber,
        provider: provider.id,
        exportBundle: providerResult.exportBundle ?? null,
      };
    }),

  /**
   * US-033.4 — list every submission attempt for a deal, newest first.
   * Powers the submission-history section on /deals/$dealId. Each row
   * carries the immutable payload snapshot + status + decision reason
   * so the athlete (or compliance) can see exactly what was sent and
   * how NIL Go responded.
   */
  listSubmissions: protectedProcedure
    .input(z.object({ dealId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      // Ownership check via the deal — same shape as the other endpoints.
      const deal = await db.deal.findFirst({
        where: { id: input.dealId, athleteId: session.user.id },
        select: { id: true },
      });
      if (!deal) throw new ORPCError('NOT_FOUND', { message: 'Deal not found' });

      const rows = await db.nilGoSubmission.findMany({
        where: { dealId: input.dealId },
        orderBy: [{ revisionNumber: 'desc' }, { createdAt: 'desc' }],
      });
      return rows.map((r: any) => ({
        id: r.id,
        status: r.status as
          | 'prepared'
          | 'submitted'
          | 'under_review'
          | 'approved'
          | 'needs_revision'
          | 'rejected'
          | 'cancelled',
        revisionNumber: r.revisionNumber,
        provider: r.provider,
        externalSubmissionId: r.externalSubmissionId,
        submittedAt: r.submittedAt?.toISOString() ?? null,
        decidedAt: r.decidedAt?.toISOString() ?? null,
        decisionReason: r.decisionReason,
        auditLog: r.auditLog,
        createdAt: r.createdAt.toISOString(),
        updatedAt: r.updatedAt.toISOString(),
      }));
    }),

  /**
   * US-033.4 — athlete records NIL Go's decision on a submission. For the
   * `export_only` provider NIL Go has no callback path; the athlete logs
   * what NIL Go's UI showed them so the deal's history reflects reality.
   *
   * Transitions allowed (anything else throws):
   *   submitted | under_review → under_review | approved | needs_revision | rejected
   *   needs_revision → cancelled    (athlete withdraws revision)
   *   any → cancelled               (athlete withdraws)
   *
   * `approved` is terminal — the snapshot becomes the source of truth and
   * a future revision will only happen by resubmitting (new row, new
   * revisionNumber). We never mutate an approved row.
   */
  recordDecision: protectedProcedure
    .input(
      z.object({
        submissionId: z.string(),
        status: z.enum(['under_review', 'approved', 'needs_revision', 'rejected', 'cancelled']),
        reason: z.string().max(2000).optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const existing = await db.nilGoSubmission.findFirst({
        where: { id: input.submissionId, athleteId: session.user.id },
      });
      if (!existing) throw new ORPCError('NOT_FOUND', { message: 'Submission not found' });

      if (existing.status === 'approved' && input.status !== 'cancelled') {
        throw new ORPCError('BAD_REQUEST', {
          message: 'Approved submissions are immutable. Resubmit (new revision) to capture a new decision.',
        });
      }

      const decidedAt =
        input.status === 'approved' ||
        input.status === 'needs_revision' ||
        input.status === 'rejected' ||
        input.status === 'cancelled'
          ? new Date()
          : null;

      const row = await db.nilGoSubmission.update({
        where: { id: input.submissionId },
        data: {
          status: input.status,
          decisionReason: input.reason ?? existing.decisionReason,
          decidedAt: decidedAt ?? existing.decidedAt,
        },
      });

      await writeAuditLog({
        action: 'nil_go.decision_recorded',
        category: 'compliance',
        actor: {
          id: session.user.id,
          name: session.user.name ?? null,
          email: session.user.email ?? null,
          role: session.user.role ?? null,
        },
        target: { type: 'nilGoSubmission', id: row.id, name: null },
        context: {
          previousStatus: existing.status,
          newStatus: input.status,
          revisionNumber: existing.revisionNumber,
          reason: input.reason ?? null,
        },
      });

      return {
        id: row.id,
        status: row.status,
        decisionReason: row.decisionReason,
        decidedAt: row.decidedAt?.toISOString() ?? null,
      };
    }),
};
